import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { AlertTriangle, Lightbulb, ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const COLORS = {
  obstacle: '#E94B6E',
  opportunity: '#A6D64D',
};

const Section = styled.section`
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 10px 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ClusterCard = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 10px;
`;

const ClusterHeader = styled.button`
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const ChevronWrap = styled.span`
  flex-shrink: 0;
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.45);
`;

const ClusterHeaderBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const ClusterTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClusterTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 650;
  color: #fff;
  flex: 1;
`;

const ItemCount = styled.span`
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.45);
  flex-shrink: 0;
`;

const ClusterDescription = styled.div`
  margin-top: 4px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.4;
`;

const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const ItemRow = styled.li`
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  &:last-child {
    border-bottom: none;
  }
`;

const ItemButton = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.88);
  text-align: left;
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-top: 7px;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

const LensTag = styled.span`
  display: inline-block;
  margin-top: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.75);
  background: rgba(255, 255, 255, 0.08);
`;

const Desc = styled.div`
  padding: 4px 0 0 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.58);
  line-height: 1.45;
`;

function ClusterItemRow({ item, color, showDescription, onItemClick, unnamedLabel }) {
  return (
    <ItemRow>
      <ItemButton type="button" onClick={() => onItemClick?.(item)}>
        <Dot $color={color} />
        <span style={{ flex: 1 }}>
          <span>{item?.title || unnamedLabel}</span>
          <LensTag>{item?.lens || '-'}</LensTag>
          {showDescription && item?.description && <Desc>{item.description}</Desc>}
        </span>
      </ItemButton>
    </ItemRow>
  );
}

function ClusterSection({ title, clusters, icon, color, itemsById, showDescription, onItemClick, t }) {
  const [expanded, setExpanded] = useState({});

  if (!clusters?.length) return null;

  const toggle = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Section>
      <SectionTitle>
        {icon}
        {title}
      </SectionTitle>
      {clusters.map((cluster, index) => {
        const mappedItems = (cluster.itemIds || [])
          .map((id) => itemsById.get(id))
          .filter(Boolean);
        if (!mappedItems.length) return null;

        const clusterKey = `${cluster.title || 'cluster'}-${index}`;
        const isOpen = !!expanded[clusterKey];

        return (
          <ClusterCard key={clusterKey}>
            <ClusterHeader type="button" onClick={() => toggle(clusterKey)} aria-expanded={isOpen}>
              <ChevronWrap>
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </ChevronWrap>
              <ClusterHeaderBody>
                <ClusterTitleRow>
                  <ClusterTitle>{cluster.title || t('lenses.clusterDefaultTitle')}</ClusterTitle>
                  <ItemCount>{mappedItems.length}</ItemCount>
                </ClusterTitleRow>
                {cluster.description && <ClusterDescription>{cluster.description}</ClusterDescription>}
              </ClusterHeaderBody>
            </ClusterHeader>
            {isOpen && (
              <ItemList>
                {mappedItems.map((item) => (
                  <ClusterItemRow
                    key={item.id}
                    item={item}
                    color={color}
                    showDescription={showDescription}
                    onItemClick={onItemClick}
                    unnamedLabel={t('lenses.unnamedItem')}
                  />
                ))}
              </ItemList>
            )}
          </ClusterCard>
        );
      })}
    </Section>
  );
}

export default function PivotClusteredListView({
  obstacleClusters = [],
  opportunityClusters = [],
  items = [],
  showDescription = true,
  onItemClick,
}) {
  const { t } = useTranslation();
  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  return (
    <div>
      <ClusterSection
        title={t('lenses.obstacleClustersTitle')}
        clusters={obstacleClusters}
        icon={<AlertTriangle size={14} color={COLORS.obstacle} />}
        color={COLORS.obstacle}
        itemsById={itemsById}
        showDescription={showDescription}
        onItemClick={onItemClick}
        t={t}
      />
      <ClusterSection
        title={t('lenses.opportunityClustersTitle')}
        clusters={opportunityClusters}
        icon={<Lightbulb size={14} color={COLORS.opportunity} />}
        color={COLORS.opportunity}
        itemsById={itemsById}
        showDescription={showDescription}
        onItemClick={onItemClick}
        t={t}
      />
    </div>
  );
}
